import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Pagination, Row, Select, Space, Table, Typography, message, Col } from "antd";
import type { PaginationProps, TableProps } from "antd";
import { BsPencilSquare } from "react-icons/bs";
import { usePagination } from "src/hook/usePagination";
import { useDidMount } from "src/hook/useDidMount";
import { useShallow } from "zustand/react/shallow";
import { CommonSuccess } from "src/entity/common/menus-enum.enum";
import { profileStore } from "src/stores/profile-store";
import { setupWasteWaterStore } from "src/stores/waste-water-info-store";
import {
  FilterManualWasteWater,
  ResponseManualWasteWater,
  UpdateManualWasteWater,
  ObjectEmissionWithValue,
} from "src/entity/waste-water/manual-waste-water.entity";
import { utilsStore } from "src/stores/utils-store";
import ManualVocWaterModal from "./ManualVocWaterModal";
import { manualWasteWaterStore } from "src/stores/manual-waste-water";
import { reportWasteWaterStore } from "src/stores/waste-water-report";
import { isNull, omitBy } from "lodash";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canEditTable, canViewTable } from "../../PermissionCheck/PermissionCheck";
import { formatNumber } from "src/utils/process-number";
import { convertEmissionWord } from "src/entity/common/global.entity";
import { optionsSelectYears } from "src/utils/array";
import { addCenterAlign } from "src/components/AntdCustomPart/TableTitle.tsx/TableTitle";

const { Text } = Typography;
interface ManualVocWaterTableProps {}

const ManualVocWaterTable: React.FC<ManualVocWaterTableProps> = ({}) => {
  const currentYear = new Date().getFullYear();
  const [messageApi, contextHolder] = message.useMessage();

  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [visibleModal, setVisibleModal] = useState(false);
  const [isEdit, setIsEdit] = useState<ResponseManualWasteWater | null>(null);
  const [tableKey, setTableKey] = useState(0);

  const { permissions } = useNavigationMenu();

  const profile = profileStore(useShallow((state) => state.profile));
  const getAreaInCompany = utilsStore(useShallow((state) => state.getAreaInCompany));
  const dataAreaInCompany = utilsStore(useShallow((state) => state.dataAreaInCompany));
  // Group 1. masterdata, utils data
  const getAllSettingWasteWater = setupWasteWaterStore(useShallow((state) => state.getAllSettingWasteWater));
  const dataSettingWasteWater = setupWasteWaterStore(useShallow((state) => state.dataSettingWasteWater));
  const getFilterColumnManualWasteWater = manualWasteWaterStore(useShallow((state) => state.getFilterColumnManualWasteWater));
  const filterWasteWater = manualWasteWaterStore(useShallow((state) => state.filterWasteWater));

  // Group 2. Main Service, main data
  const searchManualWasteWater = manualWasteWaterStore(useShallow((state) => state.searchManualWasteWater));
  const dataManualWasteWater = manualWasteWaterStore(useShallow((state) => state.dataManualWasteWater));
  const totalDocs = dataManualWasteWater.total;
  const loadingWasteWater = manualWasteWaterStore(useShallow((state) => state.loadingWasteWater));

  const updateManualWasteWater = manualWasteWaterStore(useShallow((state) => state.updateManualWasteWater));
  const loadingManageWasteWater = manualWasteWaterStore(useShallow((state) => state.loadingManageWasteWater));
  const calculateReportVocWasteWater = reportWasteWaterStore(useShallow((state) => state.calculateReportVocWasteWater));

  const clearError = manualWasteWaterStore(useShallow((state) => state.clearError));
  const errorWasteWater = manualWasteWaterStore(useShallow((state) => state.errorWasteWater));

  // console.log(" filterWasteWater : ", filterWasteWater);

  const getMasterLabel = (type: string, text: string) => {
    if (!text || typeof text != "string") return "";

    if (type === "emissionControl" && dataManualWasteWater) {
      const optValue = dataSettingWasteWater.find((item) => item._id === text);
      return optValue?.emissionControl;
    } else if (type === "efficiency" && dataManualWasteWater) {
      const optValue = dataSettingWasteWater.find((item) => item._id === text);
      return optValue?.efficiency;
    } else if (type === "area" && dataAreaInCompany) {
      const optValue = dataAreaInCompany.find((item) => item._id === text);
      return optValue?.areaName;
    } else return text;
  };

  const onClickEdit = (item: ResponseManualWasteWater) => {
    Object.keys(item.cloneEmission).forEach((key) => {
      if (!item.cloneEmission[key as keyof ObjectEmissionWithValue].emissionBeforeTreatment) {
        item.cloneEmission[key as keyof ObjectEmissionWithValue].emissionBeforeTreatment = "0";
      }
    });

    setIsEdit(item);
    setVisibleModal(true);
  };

  const onUpdateKeyValue = (key: keyof ObjectEmissionWithValue, value: string | number) => {
    const tmpIsEdit = { ...isEdit } as ResponseManualWasteWater;
    tmpIsEdit.cloneEmission[key].emissionBeforeTreatment = value;
    setIsEdit(tmpIsEdit);
  };

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    data: sourceData,
  } = usePagination<ResponseManualWasteWater, FilterManualWasteWater>({
    action: searchManualWasteWater,
    remoteData: dataManualWasteWater.data,
    initialFilter: { year: [selectedYear] },
  });

  const currentFilterExcludePage = useMemo(() => {
    const result = omitBy(filter, (value, key) => key === "page" || key === "year" || key === "limit" || isNull(value));
    return Object.keys(result).length;
  }, [filter]);

  const wasteWaterFilter = useMemo(() => {
    if ((dataManualWasteWater.data?.length === 0 || filterWasteWater.wasteWaterUnit.length === 0) && currentFilterExcludePage == 0)
      return [];
    return filterWasteWater.wasteWaterUnit.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterWasteWater.wasteWaterUnit, dataManualWasteWater.data]);
  const areaFilter = useMemo(() => {
    if ((dataManualWasteWater.data?.length === 0 || filterWasteWater.area.length === 0) && currentFilterExcludePage == 0) return [];
    return filterWasteWater.area.map((item) => {
      const findLabel = dataAreaInCompany.find((e) => e._id === item);
      return {
        text: findLabel?.areaName,
        value: item,
      };
    });
  }, [filterWasteWater.area, dataManualWasteWater.data, dataAreaInCompany]);
  const emissionControlFilter = useMemo(() => {
    if ((dataManualWasteWater.data?.length === 0 || filterWasteWater.emissionControl.length === 0) && currentFilterExcludePage == 0)
      return [];
    return filterWasteWater.emissionControl.map((item) => {
      const findLabel = dataSettingWasteWater.find((e) => e._id === item);
      return {
        text: findLabel?.emissionControl,
        value: item,
      };
    });
  }, [filterWasteWater.emissionControl, dataManualWasteWater.data, dataSettingWasteWater]);

  const efficiencyFilter = useMemo(() => {
    if ((dataManualWasteWater.data?.length === 0 || filterWasteWater.efficiency.length === 0) && currentFilterExcludePage == 0) return [];
    return filterWasteWater.efficiency.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterWasteWater.efficiency, dataManualWasteWater.data]);

  const emissionFilter = useMemo(() => {
    if ((dataManualWasteWater.data?.length === 0 || filterWasteWater.emission.length === 0) && currentFilterExcludePage == 0) return [];
    return filterWasteWater.emission.map((item) => {
      return {
        text: item.toUpperCase(),
        value: item,
      };
    });
  }, [filterWasteWater.emission, dataManualWasteWater.data]);

  const handleTableChange: TableProps<ResponseManualWasteWater>["onChange"] = (_, filters) => {
    onChange(filters);
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;
  const columns: TableProps<ResponseManualWasteWater>["columns"] = [
    {
      title: "No",
      dataIndex: "no",
      key: "no",
      render: (text, record, index) => {
        const obj = {
          children: <Text>{getIndex(+text)}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      align: "left",
    },
    {
      title: "Wastewater Unit",
      dataIndex: "wasteWaterUnit",
      key: "wasteWaterUnit",
      render: (text, record, index) => {
        const obj = {
          children: <Text>{text}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      filters: !canViewTable(permissions) ? [] : wasteWaterFilter,
      align: "left",
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
      render: (text, record, index) => {
        const obj = {
          children: <Text>{getMasterLabel("area", text)}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      filters: !canViewTable(permissions) ? [] : areaFilter,
      align: "left",
    },
    {
      title: "Emission Control",
      dataIndex: "emissionControl",
      key: "emissionControl",
      render: (_, record, index) => {
        const obj = {
          children: <Text>{record?.emissionControl?.isActive ? record.emissionControl.emissionControl : ""}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      filters: !canViewTable(permissions) ? [] : emissionControlFilter,
      align: "left",
    },
    {
      title: "Efficiency (%)",
      dataIndex: "efficiency",
      key: "efficiency",
      render: (_, record, index) => {
        const obj = {
          children: <Text>{record?.emissionControl?.isActive ? record.emissionControl.efficiency : ""}</Text>,
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1]._id !== record._id) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item._id === record._id).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      filters: !canViewTable(permissions) ? [] : efficiencyFilter,
      align: "left",
    },
    {
      title: "Emission",
      key: "emission",
      render: (_, record) => <Text>{convertEmissionWord(record.activeEmission)}</Text>,
      align: "left",
      filters: !canViewTable(permissions) ? [] : emissionFilter,
    },
    {
      title: "Unit",
      key: "unit",
      render: (_, record) => <Text>{record.unit}</Text>,
      align: "left",
    },
    {
      title: "Emission Before Treatment per Year",
      key: "emissionBeforeTreatment",
      render: (_, record) => <Text>{formatNumber(record.emissionBeforeTreatmentValue as number, 10)}</Text>,
      align: "left",
    },
    {
      title: "Action",
      key: "action",
      render: (text, record, index) => {
        const obj = {
          children: (
            <Space size="middle" direction={"horizontal"}>
              <Button
                className="border-transparent bg-transparent"
                size={"small"}
                onClick={() => onClickEdit(record)}
                disabled={!canEditTable(permissions)}
              >
                <BsPencilSquare size={18} />
              </Button>
            </Space>
          ),
          props: {
            rowSpan: 1,
          },
        };
        if (index === 0 || (sourceData as any)[index - 1].no !== record.no) {
          obj.props.rowSpan = (sourceData as any).filter((item: any) => item.no === record.no).length;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
      align: "left",
    },
  ];

  const getColumnFilter = () => {
    getFilterColumnManualWasteWater(selectedYear);
  };

  const initialRequestDidMount = (isDidMount?: boolean) => {
    getAreaInCompany(profile?.activeCompany._id as string);
    getAllSettingWasteWater();
    getColumnFilter();
    searchManualWasteWater(isDidMount === true ? { ...filter, page, limit: pageSize } : { page, limit: pageSize, year: [selectedYear] });
  };

  useDidMount(() => {
    initialRequestDidMount(true);
  });

  useEffect(() => {
    if (profile?.activeCompany) {
      setPage(1);
      setTableKey(tableKey + 1);
    }
  }, [profile?.activeCompany, selectedYear]);

  useEffect(() => {
    if (tableKey > 0) {
      initialRequestDidMount(false);
    }
  }, [tableKey]);

  const onChangePage = (val: number) => {
    setPage(val);
  };

  const paginationProps: PaginationProps = {
    size: "small",
    pageSize: pageSize,
    current: page,
    total: totalDocs || 0,
    defaultCurrent: 1,
    onChange: onChangePage,
    showSizeChanger: false,
  };

  const closeModal = () => {
    setVisibleModal(false);
    clearError("errorWasteWater");
    setIsEdit(null);
  };

  const submitSetupHeatingValue = async () => {
    // console.log("Process final data : ", isEdit);
    const processData = { emission: isEdit?.cloneEmission } as UpdateManualWasteWater;
    updateManualWasteWater(isEdit?._id as string, processData).then(onFinishAction);
  };

  const handleChangeYear = (year: string) => {
    // console.log("Year : ", year);
    onChange({ ...filter, year: [year] });
    setSelectedYear(year);
  };

  const onFinishAction = (response?: unknown) => {
    // console.log("response : ", response);
    if (typeof response === "string") {
      // warningAlert(response as string);
      return;
    } else {
      messageApi.open({
        type: "success",
        content: CommonSuccess.SAVED_SUCCESSFULL,
      });
      calculateReportVocWasteWater(isEdit?._id as string);
      getColumnFilter();
      searchManualWasteWater({ ...filter, page, limit: pageSize, year: [selectedYear] });
      closeModal();
    }
  };

  return (
    <Row className="w-full" id={"wrap-waste-water-table"}>
      {contextHolder}
      <Row className="w-full" justify={"space-between"}>
        <Select value={selectedYear} style={{ width: 120 }} onChange={handleChangeYear} options={optionsSelectYears()} size="middle" />
      </Row>
      <Table
        columns={addCenterAlign(columns)}
        dataSource={!canViewTable(permissions) ? [] : (sourceData as ResponseManualWasteWater[])}
        className="w-full mt-4"
        loading={loading}
        pagination={false}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        rowKey={(rec, index) => `${rec._id}-${index}-${rec.no}`}
      />

      <Row className="w-full mt-4" justify={"end"}>
        <Pagination {...paginationProps} />
      </Row>

      <ManualVocWaterModal
        isEdit={isEdit}
        getMasterLabel={getMasterLabel}
        submitSetupHeatingValue={submitSetupHeatingValue}
        loadingManageWasteWater={loadingManageWasteWater}
        visibleModal={visibleModal}
        closeModal={closeModal}
        errorWasteWater={errorWasteWater}
        onUpdateKeyValue={onUpdateKeyValue}
      />
    </Row>
  );
};

export default ManualVocWaterTable;
